// ./src/components/features/ContentBlockFormModal/ContentBlockFormModal.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  TextInput,
  Textarea,
  NumberInput,
  Stack,
  Text,
  Grid,
  Alert,
  InputLabel,
  Group,
  Image,
  Paper,
  FileInput,
  Loader,
  Title,
  Box,
} from "@mantine/core";

import { ContentBlock, ContentBlockInput } from "@utils/api";

import {
  resizeImageIfNeeded,
  validateImageFile,
  getSupportedImageTypesString,
  getSupportedFormatsDescription,
  createResizeNote,
  createUploadNotes,
  DEFAULT_IMAGE_RESIZE_CONFIG,
  type ImageResizeConfig,
} from "@/utils";

import {
  validateVideoFile,
  getVideoMetadata,
  validateVideoDuration,
  getSupportedVideoTypesString,
  getSupportedVideoFormatsDescription,
  createVideoUploadNotes,
  generateVideoThumbnail,
  formatVideoDuration,
  formatFileSize,
  type VideoMetadata,
} from "@/utils";

import { UnsplashCollectionBlockSettings } from "@/components/features/ContentBlockTypeSettings/UnsplashCollectionBlockSettings";
import { UnsplashCollectionBlockSettings as UnsplashCollectionBlockSettingsData } from "@/types/unsplashTypes";

import { useUploadedFiles } from "@/hooks";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

import {
  AppModal,
  AppIcon,
  AppButton,
  RecurrenceSelector,
  RecurrenceDaysSelector,
} from "@/components/generic";
import { ContentTypeSelector, ContentType } from "../ContentTypeSelector";
import { StatusSelector, StatusValue } from "../../StatusSelector";
import { DateInput, TimePicker } from "@mantine/dates";
import {
  CountdownBlockSettings,
  CountdownBlockSettingsData,
  CountupBlockSettings,
  CountupBlockSettingsData,
} from "@/components/features";

export interface ContentBlockFormModalProps {
  opened: boolean;
  onClose: () => void;
  contentBlock?: ContentBlock | null;
  venueId?: string;
  onCreateContentBlock?: (
    contentBlockData: ContentBlockInput,
  ) => Promise<ContentBlock | null>;
  onUpdateContentBlock?: (
    contentBlockId: string,
    contentBlockData: ContentBlockInput,
  ) => Promise<ContentBlock | null>;
  isCreating?: boolean;
  isUpdating?: boolean;
  lastError?: string | null;
  onClearError?: () => void;
}

interface ContentBlockFormData {
  name: string;
  description: string;
  contentType: ContentType | null;
  contentData: string;
  fileUrl: string;
  uploadedFileId: string | null;
  durationSeconds: number | null;
  startDatetime?: string | null | undefined;
  endDatetime?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  recurrencePattern?: string | null | undefined;
  recurrenceDays?: string | null | undefined;
  blockSettings?: string | null | undefined;
  status: StatusValue;
}

const initialFormData: ContentBlockFormData = {
  name: "",
  description: "",
  contentType: null,
  contentData: "",
  fileUrl: "",
  uploadedFileId: null,
  durationSeconds: 10,
  startDatetime: null,
  endDatetime: null,
  startTime: null,
  endTime: null,
  recurrencePattern: "NONE",
  recurrenceDays: null,
  blockSettings: null,
  status: 100,
};

// Helper functions for block settings
const parseBlockSettings = <T = any,>(
  settingsString: string | null | undefined,
): T | null => {
  if (!settingsString) return null;
  try {
    return JSON.parse(settingsString) as T;
  } catch (error) {
    console.error("Failed to parse block settings:", error);
    return null;
  }
};

const stringifyBlockSettings = (settings: any): string | null => {
  if (!settings) return null;
  try {
    return JSON.stringify(settings);
  } catch (error) {
    console.error("Failed to stringify block settings:", error);
    return null;
  }
};

export const ContentBlockFormModal: React.FC<ContentBlockFormModalProps> = ({
  opened,
  onClose,
  contentBlock,
  venueId,
  onCreateContentBlock,
  onUpdateContentBlock,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<ContentBlockFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Partial<ContentBlockFormData>
  >({});
  const [blockSettingsErrors, setBlockSettingsErrors] = useState<any>({});
  const [resizeConfig] = useState<ImageResizeConfig>(
    DEFAULT_IMAGE_RESIZE_CONFIG,
  );
  const [resizeNote, setResizeNote] = useState<string>("");
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(
    null,
  );
  const [, /*videoThumbnail*/ setVideoThumbnail] = useState<string | null>(
    null,
  );
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isDeletingFile, setIsDeletingFile] = useState(false);

  // Track uploaded files during the current session that need cleanup on cancel
  const uploadedFileIdForCleanup = useRef<string | null>(null);
  const originalUploadedFileId = useRef<string | null>(null);

  const uploadedFilesHook = useUploadedFiles();

  const isEditMode = Boolean(contentBlock) && Boolean(contentBlock?.id);
  const isSubmitting = isCreating || isUpdating;
  const hasFile = Boolean(formData.fileUrl);

  useEffect(() => {
    if (opened) {
      if (contentBlock) {
        // Store the original uploaded file ID when editing
        originalUploadedFileId.current = contentBlock.uploadedFileId || null;
        uploadedFileIdForCleanup.current = null;

        setFormData({
          name: contentBlock.name || "",
          description: contentBlock.description || "",
          contentType: (contentBlock.contentType as ContentType) || null,
          contentData: contentBlock.contentData || "",
          fileUrl: contentBlock.fileUrl || "",
          uploadedFileId: contentBlock.uploadedFileId || null,
          durationSeconds: contentBlock.durationSeconds || null,
          startDatetime: contentBlock.startDatetime
            ? dayjs(contentBlock.startDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          endDatetime: contentBlock.endDatetime
            ? dayjs(contentBlock.endDatetime).format("YYYY-MM-DDTHH:mm:00Z")
            : null,
          startTime: contentBlock.startTime
            ? dayjs(contentBlock.startTime).format("HH:mm")
            : null,
          endTime: contentBlock.endTime
            ? dayjs(contentBlock.endTime).format("HH:mm")
            : null,
          recurrencePattern: contentBlock.recurrencePattern || "NONE",
          recurrenceDays: contentBlock.recurrenceDays || null,
          blockSettings: contentBlock.blockSettings || null,
          status: (contentBlock.status as StatusValue) || 100,
        });
      } else {
        // Creating new block - reset tracking
        originalUploadedFileId.current = null;
        uploadedFileIdForCleanup.current = null;
        setFormData({ ...initialFormData });
      }
      setValidationErrors({});
      setBlockSettingsErrors({});
      setResizeNote("");
      setVideoMetadata(null);
      setVideoThumbnail(null);
      onClearError?.();
    }
  }, [opened, contentBlock, onClearError, venueId]);

  const validateForm = (): boolean => {
    const newErrors: any = {};
    const newBlockSettingsErrors: any = {};

    if (!formData.name.trim()) {
      newErrors.name = "Content block name is required";
    }

    if (!formData.contentType) {
      newErrors.contentType = "Content type is required";
    }

    if (
      formData.contentType === "HTML" ||
      formData.contentType === "MARKDOWN"
    ) {
      if (!formData.contentData.trim()) {
        newErrors.contentData =
          "Content data is required for this content type";
      }
    }

    if (formData.contentType === "IMAGE") {
      if (!formData.fileUrl.trim()) {
        newErrors.fileUrl = "Image file is required for this content type";
      }
      if (!formData.uploadedFileId) {
        newErrors.fileUrl = "Image file is required for this content type";
      }
    }

    if (formData.contentType === "VIDEO") {
      if (!formData.fileUrl.trim()) {
        newErrors.fileUrl = "Video file is required for this content type";
      }
      if (!formData.uploadedFileId) {
        newErrors.fileUrl = "Video file is required for this content type";
      }
    }

    if (formData.contentType === "EXTERNAL_VIDEO") {
      if (!formData.fileUrl.trim()) {
        newErrors.fileUrl = "Video URL is required for this content type";
      } else if (!isValidUrl(formData.fileUrl.trim())) {
        newErrors.fileUrl = "Please enter a valid video URL";
      }
    }

    if (formData.contentType === "IFRAME") {
      if (!formData.fileUrl.trim()) {
        newErrors.fileUrl = "URL is required for this content type";
      } else if (!isValidUrl(formData.fileUrl.trim())) {
        newErrors.fileUrl = "Please enter a valid URL";
      }
    }

    if (formData.contentType === "COUNTDOWN") {
      const countdownSettings = parseBlockSettings<CountdownBlockSettingsData>(
        formData.blockSettings,
      );

      if (!countdownSettings) {
        newErrors.blockSettings = "Countdown settings are required";
      } else {
        if (!countdownSettings.targetDateTime) {
          newBlockSettingsErrors.targetDateTime =
            "Target date/time is required";
        } else {
          const targetDate = dayjs(countdownSettings.targetDateTime);
          if (targetDate.isBefore(dayjs())) {
            newBlockSettingsErrors.targetDateTime =
              "Target date must be in the future";
          }
        }
      }
    }

    if (formData.contentType === "COUNTUP") {
      const countupSettings = parseBlockSettings<CountupBlockSettingsData>(
        formData.blockSettings,
      );

      if (!countupSettings) {
        newErrors.blockSettings = "Countup settings are required";
      } else {
        if (!countupSettings.startDateTime) {
          newBlockSettingsErrors.startDateTime = "Start date/time is required";
        }
      }
    }

    if (formData.contentType === "UNSPLASH_COLLECTION") {
      const unsplashSettings =
        parseBlockSettings<UnsplashCollectionBlockSettingsData>(
          formData.blockSettings,
        );

      if (!unsplashSettings?.collectionId) {
        newBlockSettingsErrors.collectionId =
          "Please select an Unsplash collection";
      }
    }

    if (formData.durationSeconds !== null && formData.durationSeconds <= 0) {
      newErrors.durationSeconds = "Duration must be greater than 0";
    }

    if (formData.startDatetime && formData.endDatetime) {
      if (formData.startDatetime >= formData.endDatetime) {
        newErrors.endDatetime = "End date must be after start date";
      }
    }

    if (formData.startTime && formData.endTime) {
      if (formData.startTime >= formData.endTime) {
        newErrors.endTime = "End time must be after start time";
      }
    }

    if (
      formData.recurrencePattern === "WEEKLY" ||
      formData.recurrencePattern === "MONTHLY"
    ) {
      if (!formData.recurrenceDays || formData.recurrenceDays.trim() === "") {
        newErrors.recurrenceDays =
          "Recurrence days are required for weekly and monthly patterns";
      }
    }

    setValidationErrors(newErrors);
    setBlockSettingsErrors(newBlockSettingsErrors);
    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(newBlockSettingsErrors).length === 0
    );
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleInputChange = (
    field: keyof ContentBlockFormData,
    value: string | number | ContentType | StatusValue | null,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleContentTypeChange = (contentType: string | null) => {
    handleInputChange("contentType", contentType);
    if (contentType !== formData.contentType) {
      handleInputChange("contentData", "");
      handleInputChange("fileUrl", "");
      handleInputChange("uploadedFileId", null);
      handleInputChange("blockSettings", null);
      setResizeNote("");
      setVideoMetadata(null);
      setVideoThumbnail(null);
      setBlockSettingsErrors({});
    }
  };

  const handleCountdownSettingsChange = (
    settings: CountdownBlockSettingsData,
  ) => {
    const settingsString = stringifyBlockSettings(settings);
    handleInputChange("blockSettings", settingsString);
    setBlockSettingsErrors({});
  };

  const handleCountupSettingsChange = (settings: CountupBlockSettingsData) => {
    const settingsString = stringifyBlockSettings(settings);
    handleInputChange("blockSettings", settingsString);
    setBlockSettingsErrors({});
  };

  const handleUnsplashSettingsChange = (
    settings: UnsplashCollectionBlockSettingsData,
  ) => {
    const settingsString = stringifyBlockSettings(settings);
    handleInputChange("blockSettings", settingsString);
    setBlockSettingsErrors({});
  };

  const handleImageFileSelect = async (file: File | null) => {
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.isValid) {
      setValidationErrors((prev) => ({
        ...prev,
        fileUrl: validation.error || "",
      }));
      return;
    }

    setIsUploadingFile(true);
    setResizeNote("");

    try {
      const resizeResult = await resizeImageIfNeeded(file, resizeConfig);

      if (
        resizeResult.wasResized &&
        resizeResult.newDimensions &&
        resizeResult.originalDimensions
      ) {
        setResizeNote(
          createResizeNote(
            resizeResult.originalDimensions,
            resizeResult.newDimensions,
          ),
        );
      }

      const uploadResult = await uploadedFilesHook.create({
        name: resizeResult.file.name,
        originalFilename: file.name,
        mimeType: resizeResult.file.type,
        notes: createUploadNotes(
          resizeResult.wasResized,
          resizeResult.originalDimensions,
          resizeResult.newDimensions,
        ),
        files: [resizeResult.file],
      });

      if (uploadResult) {
        // Track this upload for potential cleanup on cancel
        // Only track if it's a new upload (not the original file from editing)
        if (uploadResult.id !== originalUploadedFileId.current) {
          uploadedFileIdForCleanup.current = uploadResult.id || null;
        }

        setFormData((prev) => ({
          ...prev,
          fileUrl: uploadResult.fileUrl || "",
          uploadedFileId: uploadResult.id || null,
        }));
        setValidationErrors((prev) => ({ ...prev, fileUrl: "" }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          fileUrl: uploadedFilesHook.lastError || "Failed to upload image",
        }));
      }
    } catch (error) {
      setValidationErrors((prev) => ({
        ...prev,
        fileUrl:
          error instanceof Error ? error.message : "Failed to process image",
      }));
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleVideoFileSelect = async (file: File | null) => {
    if (!file) return;

    // Validate video file
    const validation = validateVideoFile(file);
    if (!validation.isValid) {
      setValidationErrors((prev) => ({
        ...prev,
        fileUrl: validation.error || "",
      }));
      return;
    }

    setIsUploadingFile(true);
    setVideoMetadata(null);
    setVideoThumbnail(null);

    try {
      // Get video metadata
      const metadata = await getVideoMetadata(file);

      // Validate duration if needed
      if (metadata.duration) {
        const durationValidation = validateVideoDuration(metadata.duration);
        if (!durationValidation.isValid) {
          setValidationErrors((prev) => ({
            ...prev,
            fileUrl: durationValidation.error || "",
          }));
          setIsUploadingFile(false);
          return;
        }

        // Auto-fill duration if not set
        if (!formData.durationSeconds) {
          handleInputChange("durationSeconds", Math.ceil(metadata.duration));
        }
      }

      // Generate thumbnail
      try {
        const thumbnail = await generateVideoThumbnail(file);
        setVideoThumbnail(thumbnail);
      } catch (error) {
        console.warn("Failed to generate video thumbnail:", error);
      }

      // Upload the video file
      const uploadResult = await uploadedFilesHook.create({
        name: file.name,
        originalFilename: file.name,
        mimeType: file.type,
        notes: createVideoUploadNotes(metadata),
        files: [file],
      });

      if (uploadResult) {
        // Track this upload for potential cleanup on cancel
        // Only track if it's a new upload (not the original file from editing)
        if (uploadResult.id !== originalUploadedFileId.current) {
          uploadedFileIdForCleanup.current = uploadResult.id || null;
        }

        setFormData((prev) => ({
          ...prev,
          fileUrl: uploadResult.fileUrl || "",
          uploadedFileId: uploadResult.id || null,
        }));
        setVideoMetadata(metadata);
        setValidationErrors((prev) => ({ ...prev, fileUrl: "" }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          fileUrl: uploadedFilesHook.lastError || "Failed to upload video",
        }));
      }
    } catch (error) {
      setValidationErrors((prev) => ({
        ...prev,
        fileUrl:
          error instanceof Error ? error.message : "Failed to process video",
      }));
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!formData.uploadedFileId) return;
    setIsDeletingFile(true);
    try {
      await uploadedFilesHook.remove({ id: formData.uploadedFileId });

      // Clear the cleanup tracking if we're removing the file we just uploaded
      if (uploadedFileIdForCleanup.current === formData.uploadedFileId) {
        uploadedFileIdForCleanup.current = null;
      }

      setFormData((prev) => ({ ...prev, fileUrl: "", uploadedFileId: null }));
      setResizeNote("");
      setVideoMetadata(null);
      setVideoThumbnail(null);
    } catch (error: any) {
      console.error("Failed to remove file", error.message);
      setFormData((prev) => ({ ...prev, fileUrl: "", uploadedFileId: null }));
      setResizeNote("");
      setVideoMetadata(null);
      setVideoThumbnail(null);
    } finally {
      setIsDeletingFile(false);
    }
  };

  const getDisplayError = (
    field: keyof ContentBlockFormData,
  ): string | number | null | undefined => {
    return validationErrors[field];
  };

  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const contentBlockData: ContentBlockInput = {
        name: formData.name,
        description: formData.description,
        contentType: formData.contentType,
        contentData: formData.contentData,
        fileUrl: formData.fileUrl,
        uploadedFileId: formData.uploadedFileId || null,
        durationSeconds: formData.durationSeconds,
        startDatetime: formData.startDatetime
          ? dayjs(formData.startDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        endDatetime: formData.endDatetime
          ? dayjs(formData.endDatetime).utc().format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        startTime: formData.startTime
          ? dayjs(`1970-01-01 ${formData.startTime}`)
              .utc()
              .format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        endTime: formData.endTime
          ? dayjs(`1970-01-01 ${formData.endTime}`)
              .utc()
              .format("YYYY-MM-DDTHH:mm:00Z")
          : null,
        recurrencePattern: formData.recurrencePattern,
        recurrenceDays: formData.recurrenceDays || null,
        blockSettings: formData.blockSettings || null,
        status: formData.status,
      };

      let result: ContentBlock | null = null;

      if (isEditMode && contentBlock && onUpdateContentBlock) {
        result = await onUpdateContentBlock(contentBlock.id, contentBlockData);
      } else if (!isEditMode && onCreateContentBlock) {
        result = await onCreateContentBlock(contentBlockData);
      }

      if (result) {
        // Success - clear cleanup tracking since the file is now associated with a block
        uploadedFileIdForCleanup.current = null;
        onClose();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = async () => {
    if (isSubmitting) return;

    // Clean up uploaded file if user uploaded during this session and is now canceling
    if (uploadedFileIdForCleanup.current) {
      try {
        await uploadedFilesHook.remove({
          id: uploadedFileIdForCleanup.current,
        });
        uploadedFileIdForCleanup.current = null;
      } catch (error: any) {
        console.error(
          "Failed to cleanup uploaded file on cancel:",
          error.message,
        );
      }
    }

    onClose();
  };

  const handleRecurrenceDaysChange = (values: string[]) => {
    const joinedValues = values.length > 0 ? values.join(",") : null;
    handleInputChange("recurrenceDays", joinedValues);
  };

  const renderImageUpload = () => {
    if (!["IMAGE"].includes(formData.contentType || "")) {
      return null;
    }

    return (
      <Stack gap="sm">
        <InputLabel mb="xs">Image File</InputLabel>
        {hasFile ? (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap="xs" style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    Current Image
                  </Text>
                  <Image
                    src={formData.fileUrl}
                    alt="Content image"
                    w={200}
                    h={150}
                    fit="contain"
                    fallbackSrc="/placeholder-image.png"
                  />
                </Stack>
                <AppButton
                  variant="danger"
                  size="sm"
                  iconName="Trash"
                  onClick={handleRemoveFile}
                  inProgress={isDeletingFile}
                  disabled={isUploadingFile || isSubmitting}
                >
                  Remove
                </AppButton>
              </Group>
              {resizeNote && (
                <Alert
                  color="blue"
                  icon={<AppIcon iconName="InfoCircle" size={16} />}
                >
                  <Text size="sm">{resizeNote}</Text>
                </Alert>
              )}
            </Stack>
          </Paper>
        ) : (
          <Stack gap="sm">
            <FileInput
              placeholder="Choose image file"
              accept={getSupportedImageTypesString()}
              onChange={handleImageFileSelect}
              disabled={isUploadingFile || isSubmitting}
              leftSection={
                isUploadingFile ? (
                  <Loader size={16} />
                ) : (
                  <AppIcon iconName="Upload" size={16} />
                )
              }
              error={getDisplayError("fileUrl")}
              size="md"
            />
            <Text size="xs" c="dimmed">
              Supported formats: {getSupportedFormatsDescription()}. Images
              larger than {resizeConfig.maxWidth}x{resizeConfig.maxHeight} will
              be automatically resized.
            </Text>
          </Stack>
        )}
      </Stack>
    );
  };

  const renderVideoUpload = () => {
    if (!["VIDEO"].includes(formData.contentType || "")) {
      return null;
    }

    return (
      <Stack gap="sm">
        <InputLabel mb="xs">Video File</InputLabel>
        {hasFile ? (
          <Paper p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start">
                <Stack gap="xs" style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    Current Video
                  </Text>

                  {formData.fileUrl && (
                    <video
                      src={formData.fileUrl}
                      controls
                      style={{ maxWidth: 300, maxHeight: 200 }}
                    />
                  )}

                  {videoMetadata && (
                    <Stack gap={4}>
                      {videoMetadata.duration && (
                        <Group gap="xs">
                          <AppIcon iconName="Clock" size={14} />
                          <Text size="xs" c="dimmed">
                            Duration:{" "}
                            {formatVideoDuration(videoMetadata.duration)}
                          </Text>
                        </Group>
                      )}
                      {videoMetadata.width && videoMetadata.height && (
                        <Group gap="xs">
                          <AppIcon iconName="Maximize" size={14} />
                          <Text size="xs" c="dimmed">
                            Dimensions: {videoMetadata.width}x
                            {videoMetadata.height}
                          </Text>
                        </Group>
                      )}
                      <Group gap="xs">
                        <AppIcon iconName="File" size={14} />
                        <Text size="xs" c="dimmed">
                          Size: {formatFileSize(videoMetadata.size)}
                        </Text>
                      </Group>
                    </Stack>
                  )}
                </Stack>

                <AppButton
                  variant="danger"
                  size="sm"
                  iconName="Trash"
                  onClick={handleRemoveFile}
                  inProgress={isDeletingFile}
                  disabled={isUploadingFile || isSubmitting}
                >
                  Remove
                </AppButton>
              </Group>
            </Stack>
          </Paper>
        ) : (
          <Stack gap="sm">
            <FileInput
              placeholder="Choose video file"
              accept={getSupportedVideoTypesString()}
              onChange={handleVideoFileSelect}
              disabled={isUploadingFile || isSubmitting}
              leftSection={
                isUploadingFile ? (
                  <Loader size={16} />
                ) : (
                  <AppIcon iconName="Upload" size={16} />
                )
              }
              error={getDisplayError("fileUrl")}
              size="md"
            />
            <Text size="xs" c="dimmed">
              Supported formats: {getSupportedVideoFormatsDescription()}.
              Maximum file size: 500MB, maximum duration: 10 minutes.
            </Text>
          </Stack>
        )}
      </Stack>
    );
  };

  const renderBlockSettings = () => {
    if (formData.contentType === "COUNTDOWN") {
      const countdownSettings = parseBlockSettings<CountdownBlockSettingsData>(
        formData.blockSettings,
      );

      return (
        <CountdownBlockSettings
          value={countdownSettings}
          onChange={handleCountdownSettingsChange}
          errors={blockSettingsErrors}
        />
      );
    }

    if (formData.contentType === "COUNTUP") {
      const countupSettings = parseBlockSettings<CountupBlockSettingsData>(
        formData.blockSettings,
      );

      return (
        <CountupBlockSettings
          value={countupSettings}
          onChange={handleCountupSettingsChange}
          errors={blockSettingsErrors}
        />
      );
    }

    if (formData.contentType === "UNSPLASH_COLLECTION") {
      const unsplashSettings =
        parseBlockSettings<UnsplashCollectionBlockSettingsData>(
          formData.blockSettings,
        );

      return (
        <UnsplashCollectionBlockSettings
          value={unsplashSettings}
          onChange={handleUnsplashSettingsChange}
          errors={blockSettingsErrors}
        />
      );
    }

    return null;
  };

  const renderContentTypeFields = () => {
    const needsContentData = ["HTML", "MARKDOWN", "MESSAGE"].includes(
      formData.contentType || "",
    );
    const needsUrlInput = ["EXTERNAL_VIDEO", "IFRAME"].includes(
      formData.contentType || "",
    );

    return (
      <Stack gap="sm">
        {needsContentData && (
          <Textarea
            label="Content Data"
            placeholder={`Enter ${formData.contentType?.toLowerCase()} content...`}
            value={formData.contentData}
            onChange={(e) => handleInputChange("contentData", e.target.value)}
            error={getDisplayError("contentData")}
            required
            withAsterisk
            rows={6}
            autosize
            minRows={3}
            maxRows={10}
            size="md"
          />
        )}
        {needsUrlInput && (
          <TextInput
            label={
              formData.contentType === "EXTERNAL_VIDEO"
                ? "Video URL"
                : "Page URL"
            }
            placeholder={
              formData.contentType === "EXTERNAL_VIDEO"
                ? "Enter video URL (e.g., https://youtube.com/watch?v=...)"
                : "Enter page URL (e.g., https://example.com)"
            }
            value={formData.fileUrl}
            onChange={(e) => handleInputChange("fileUrl", e.target.value)}
            error={getDisplayError("fileUrl")}
            required
            withAsterisk
            size="md"
          />
        )}
        {renderImageUpload()}
        {renderVideoUpload()}
      </Stack>
    );
  };

  const renderGenericErrors = () => {
    if (!lastError && !uploadedFilesHook.lastError) {
      return null;
    }

    const errorMessage = lastError || uploadedFilesHook.lastError;

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{errorMessage}</Text>
      </Alert>
    );
  };

  const getRecurrenceDaysValue = (): string[] => {
    if (!formData.recurrenceDays) return [];
    return formData.recurrenceDays.split(",").filter(Boolean);
  };

  const shouldShowRecurrenceDays =
    formData.recurrencePattern === "WEEKLY" ||
    formData.recurrencePattern === "MONTHLY";

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Content Block" : "Add New Content Block"}
      size="xl"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting || isUploadingFile,
        },
        {
          label: "Confirm",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
          disabled: isUploadingFile,
        },
      ]}
    >
      <Stack gap="md">
        <Grid>
          <Grid.Col span={12}>
            <TextInput
              label="Content Block Name"
              placeholder="Enter content block name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              error={getDisplayError("name")}
              required
              withAsterisk
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={12}>
            <Textarea
              label="Description"
              placeholder="Enter content block description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              error={getDisplayError("description")}
              rows={2}
              size="md"
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <ContentTypeSelector
              label="Content Type"
              value={formData.contentType}
              onChange={handleContentTypeChange}
              error={getDisplayError("contentType")}
              required
              withAsterisk
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6 }}>
            <NumberInput
              label="Duration (seconds)"
              placeholder="Enter duration in seconds"
              value={formData.durationSeconds || ""}
              onChange={(value) => handleInputChange("durationSeconds", value)}
              error={getDisplayError("durationSeconds")}
              min={0}
              allowNegative={false}
              allowDecimal={false}
              size="md"
            />
          </Grid.Col>
        </Grid>

        {formData.contentType && (
          <Grid>
            <Grid.Col span={12}>
              <Title order={5} mb="sm">
                Content Configuration
              </Title>
              {renderContentTypeFields()}
            </Grid.Col>
          </Grid>
        )}

        {formData.contentType && renderBlockSettings() && (
          <Grid>
            <Grid.Col span={12}>{renderBlockSettings()}</Grid.Col>
          </Grid>
        )}

        <Box>
          <Title order={5} mb="xs">
            Schedule (Optional)
          </Title>
          <Grid mb="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput
                label="Start Date"
                placeholder="Select start date"
                value={formData.startDatetime}
                onChange={(value) => handleInputChange("startDatetime", value)}
                error={getDisplayError("startDatetime")}
                clearable
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <DateInput
                label="End Date"
                placeholder="Select end date"
                value={formData.endDatetime}
                onChange={(value) => handleInputChange("endDatetime", value)}
                error={getDisplayError("endDatetime")}
                clearable
                minDate={formData.startDatetime || undefined}
                size="md"
              />
            </Grid.Col>
          </Grid>

          <Grid mb="sm">
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TimePicker
                label="Start Time"
                format="12h"
                withDropdown
                clearable
                value={formData.startTime || ""}
                onChange={(value) =>
                  handleInputChange("startTime", value || null)
                }
                error={getDisplayError("startTime")}
                size="md"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 6 }}>
              <TimePicker
                label="End Time"
                format="12h"
                withDropdown
                clearable
                value={formData.endTime || ""}
                onChange={(value) =>
                  handleInputChange("endTime", value || null)
                }
                error={getDisplayError("endTime")}
                size="md"
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={12}>
              <RecurrenceSelector
                label="Recurrence Pattern"
                placeholder="Select recurrence"
                value={formData.recurrencePattern}
                onChange={(value) =>
                  handleInputChange("recurrencePattern", value)
                }
                error={getDisplayError("recurrencePattern")}
              />
            </Grid.Col>
          </Grid>

          {shouldShowRecurrenceDays && (
            <Grid mt="sm">
              <Grid.Col span={12}>
                <RecurrenceDaysSelector
                  recurrencePattern={formData.recurrencePattern}
                  value={getRecurrenceDaysValue()}
                  onChange={handleRecurrenceDaysChange}
                  error={getDisplayError("recurrenceDays")}
                />
              </Grid.Col>
            </Grid>
          )}
        </Box>

        <Grid>
          <Grid.Col span={12}>
            <Stack gap={0}>
              <InputLabel required>Status</InputLabel>
              <StatusSelector
                value={formData.status}
                onChange={(value: number | null) =>
                  handleInputChange("status", value || 100)
                }
                error={getDisplayError("status")}
                required
              />
            </Stack>
          </Grid.Col>
        </Grid>

        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
